import { Link } from 'react-router-dom';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

const services = [
  {
    id: 1,
    title: 'Interior Design & Styling',
    description: 'Transform your space with our expert interior design services. We offer comprehensive solutions from concept to completion.',
    icon: 'ri-sofa-line',
    image: 'https://public.readdy.ai/ai/img_res/46f63d84bf8ac1995102f3ba6304d8b0.jpg',
    features: [
      'Space planning and layout optimization',
      'Color consultation and palette selection',
      'Furniture selection and procurement',
      'Décor styling and accessorizing',
      'Lighting design',
      'Custom furniture design',
    ],
  },
  {
    id: 2,
    title: 'Home Cleaning Services',
    description: 'Keep your home spotless with our professional cleaning services. From regular maintenance to deep cleaning, we handle it all.',
    icon: 'ri-drop-line',
    image: 'https://readdy.ai/api/search-image?query=professional%20cleaning%20team%20in%20uniform%20working%20in%20modern%20kitchen%20sparkling%20clean%20white%20countertops%20organized%20space%20cleaning%20supplies%20equipment%20pristine%20environment%20natural%20light%20fresh%20hygienic%20home&width=800&height=600&seq=service-detail-002&orientation=landscape',
    features: [
      'Regular maintenance cleaning',
      'Deep cleaning services',
      'Laundry and linen care',
      'Appliance cleaning',
      'Window and glass cleaning',
      'Post-renovation cleaning',
    ],
  },
  {
    id: 3,
    title: 'Home Maintenance',
    description: 'Reliable maintenance services to keep your home in perfect condition. Our skilled technicians handle all types of repairs.',
    icon: 'ri-tools-line',
    image: 'https://readdy.ai/api/search-image?query=skilled%20handyman%20in%20professional%20uniform%20fixing%20cabinet%20with%20quality%20tools%20in%20modern%20home%20interior%20reliable%20maintenance%20service%20organized%20toolbox%20clean%20workspace%20professional%20home%20repair&width=800&height=600&seq=service-detail-003&orientation=landscape',
    features: [
      'Plumbing repairs and installations',
      'Electrical work and fixtures',
      'Minor repairs and fixes',
      'Handyman services',
      'Preventive maintenance',
      'Emergency repairs',
    ],
  },
  {
    id: 4,
    title: 'Renovation & Carpentry',
    description: 'Bring your renovation dreams to life with our expert carpentry and remodeling services. Quality craftsmanship guaranteed.',
    icon: 'ri-hammer-line',
    image: 'https://readdy.ai/api/search-image?query=professional%20carpenter%20crafting%20custom%20wooden%20furniture%20in%20workshop%20quality%20wood%20materials%20precision%20tools%20modern%20craftsmanship%20natural%20wood%20tones%20clean%20organized%20workspace%20renovation%20project&width=800&height=600&seq=service-detail-004&orientation=landscape',
    features: [
      'Interior and exterior painting',
      'Flooring installation and repair',
      'Furniture assembly',
      'Custom carpentry and woodwork',
      'Kitchen and bathroom remodeling',
      'Built-in storage solutions',
    ],
  },
];

const servicesJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Art Throng Home — All Services',
  url: `${siteUrl}/services`,
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@type': 'Service',
        name: 'Interior Design & Styling',
        description:
          'Transform your space with our expert interior design services. We offer comprehensive solutions from concept to completion.',
        provider: { '@type': 'Organization', name: 'Art Throng Home' },
        areaServed: ['Goa', 'Mumbai'],
        serviceType: 'Interior Design',
      },
    },
    {
      '@type': 'ListItem',
      position: 2,
      item: {
        '@type': 'Service',
        name: 'Home Cleaning Services',
        description:
          'Keep your home spotless with our professional cleaning services. From regular maintenance to deep cleaning, we handle it all.',
        provider: { '@type': 'Organization', name: 'Art Throng Home' },
        areaServed: ['Goa', 'Mumbai'],
        serviceType: 'Home Cleaning',
      },
    },
    {
      '@type': 'ListItem',
      position: 3,
      item: {
        '@type': 'Service',
        name: 'Home Maintenance',
        description:
          'Reliable maintenance services to keep your home in perfect condition. Our skilled technicians handle all types of repairs.',
        provider: { '@type': 'Organization', name: 'Art Throng Home' },
        areaServed: ['Goa', 'Mumbai'],
        serviceType: 'Home Maintenance',
      },
    },
    {
      '@type': 'ListItem',
      position: 4,
      item: {
        '@type': 'Service',
        name: 'Renovation & Carpentry',
        description:
          'Bring your renovation dreams to life with our expert carpentry and remodeling services. Quality craftsmanship guaranteed.',
        provider: { '@type': 'Organization', name: 'Art Throng Home' },
        areaServed: ['Goa', 'Mumbai'],
        serviceType: 'Renovation',
      },
    },
  ],
};

export default function ServicesPage() {
  const scrollToBooking = () => {
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => {
      const element = document.getElementById('booking');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(servicesJsonLd) }}
      />
      {/* Hero Section */}
      <section className="py-20 bg-[#F5F1ED]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="font-serif text-5xl md:text-6xl text-[#2B2B2B] mb-6">
            Our Services
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive home solutions designed to meet all your needs, delivered by experienced professionals
          </p>
        </div>
      </section>

      {/* Pricing Table Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl text-[#2B2B2B] mb-4">Service Pricing</h2>
            <p className="text-lg text-gray-600">Transparent pricing for all property sizes</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-white rounded-2xl overflow-hidden shadow-sm">
              <thead>
                <tr className="bg-[#8B9B7E]">
                  <th className="px-6 py-5 text-left text-white font-semibold text-lg">Service Type</th>
                  <th className="px-6 py-5 text-center text-white font-semibold text-lg">1 Bedroom</th>
                  <th className="px-6 py-5 text-center text-white font-semibold text-lg">2 Bedroom</th>
                  <th className="px-6 py-5 text-center text-white font-semibold text-lg">3 Bedroom</th>
                  <th className="px-6 py-5 text-center text-white font-semibold text-lg">Villa/ 4 bed</th>
                </tr>
              </thead>
              <tbody>
                {/* Cleaning Services */}
                <tr className="border-b border-gray-200 hover:bg-[#F5F1ED] transition-colors">
                  <td className="px-6 py-5">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-[#8B9B7E]/10 rounded-lg">
                        <i className="ri-drop-line text-xl text-[#8B9B7E]"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-[#2B2B2B]">Cleaning Services</div>
                        <div className="text-sm text-gray-500">Post-stay cleaning</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <div className="font-bold text-[#2B2B2B] text-lg">€40</div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <div className="font-bold text-[#2B2B2B] text-lg">€60</div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <div className="font-bold text-[#2B2B2B] text-lg">€90</div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <div className="font-bold text-[#2B2B2B] text-lg">€149</div>
                  </td>
                </tr>

                {/* Maintenance Services */}
                <tr className="border-b border-gray-200 hover:bg-[#F5F1ED] transition-colors">
                  <td className="px-6 py-5">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-[#8B9B7E]/10 rounded-lg">
                        <i className="ri-tools-line text-xl text-[#8B9B7E]"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-[#2B2B2B]">Maintenance</div>
                        <div className="text-sm text-gray-500">Plumbing checks & minor repairs</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <div className="font-bold text-[#2B2B2B] text-lg">€99</div>
                    <div className="text-sm text-gray-500">per month</div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <div className="font-bold text-[#2B2B2B] text-lg">€109</div>
                    <div className="text-sm text-gray-500">per month</div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <div className="font-bold text-[#2B2B2B] text-lg">€149</div>
                    <div className="text-sm text-gray-500">per month</div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <div className="font-bold text-[#2B2B2B] text-lg">€179</div>
                    <div className="text-sm text-gray-500">per month</div>
                  </td>
                </tr>

                {/* Interior Design Services */}
                <tr className="hover:bg-[#F5F1ED] transition-colors">
                  <td className="px-6 py-5">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-[#8B9B7E]/10 rounded-lg">
                        <i className="ri-sofa-line text-xl text-[#8B9B7E]"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-[#2B2B2B]">Interior Design</div>
                        <div className="text-sm text-gray-500">Design consultation</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <div className="font-bold text-[#2B2B2B] text-lg">€99</div>
                    <div className="text-sm text-gray-500">per month</div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <div className="font-bold text-[#2B2B2B] text-lg">€149</div>
                    <div className="text-sm text-gray-500">per month</div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <div className="font-bold text-[#2B2B2B] text-lg">€199</div>
                    <div className="text-sm text-gray-500">per month</div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <div className="font-bold text-[#2B2B2B] text-lg">€250</div>
                    <div className="text-sm text-gray-500">per month</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-6">All prices include standard features and services</p>
            <button
              onClick={scrollToBooking}
              className="bg-[#8B9B7E] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#7a8a6d] transition-all inline-flex items-center space-x-2 whitespace-nowrap cursor-pointer"
            >
              <span>Get a Quote</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, index) => (
              <div
                key={service.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Image */}
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="relative h-96 rounded-3xl overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="flex items-center justify-center w-16 h-16 bg-[#8B9B7E]/10 rounded-2xl mb-6">
                    <i className={`${service.icon} text-4xl text-[#8B9B7E]`}></i>
                  </div>
                  <h2 className="font-serif text-4xl text-[#2B2B2B] mb-4">
                    {service.title}
                  </h2>
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <i className="ri-checkbox-circle-fill text-[#8B9B7E] text-xl flex-shrink-0 mt-0.5"></i>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={scrollToBooking}
                    className="bg-[#8B9B7E] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#7a8a6d] transition-all inline-flex items-center space-x-2 whitespace-nowrap cursor-pointer"
                  >
                    <span>Book This Service</span>
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2B2B2B]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif text-4xl md:text-5xl text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Book your service today and experience the Art Throng Home difference
          </p>
          <button
            onClick={scrollToBooking}
            className="bg-[#8B9B7E] text-white px-10 py-5 rounded-lg font-semibold hover:bg-[#7a8a6d] transition-all text-lg whitespace-nowrap cursor-pointer"
          >
            Book Now
          </button>
        </div>
      </section>
    </div>
  );
}
